import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { LayoutDashboard, Table2, UserCheck, Users2, UserX, Mail } from "lucide-react";
import RecruitingDashboard from "@/pages/recruiting/RecruitingDashboard";
import Pipeline from "@/pages/recruiting/Pipeline";
import EmailCenter from "@/pages/recruiting/EmailCenter";
import { AddLeadDialog } from "@/components/recruiting/AddLeadDialog";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";

const SCOPE_FOR_TAB = { all: "all", mine: "mine", team: "team", unassigned: "unassigned" };

export default function Recruiting() {
  const { isManager, isAdmin, isSuper, isGroupAdmin } = useAuth();
  const canTeam = isManager || isAdmin || isSuper || isGroupAdmin;
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "dashboard");
  const [pipeKey, setPipeKey] = useState(0);
  const [leadOpen, setLeadOpen] = useState(false);
  // Live filters from URL (updated by dashboard drill-through).
  const liveFilters = useMemo(() => {
    const f = {};
    ["status", "candidate_type", "recruiter_id", "organization_id", "source", "pipeline_id", "stage_id"].forEach((k) => { if (params.get(k)) f[k] = params.get(k); });
    return f;
  }, [params]);

  const TABS = [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["emails", "Send Emails", Mail],
    ["all", "All Recruiting", Table2],
    ["mine", "My Candidates", UserCheck],
    ...(canTeam ? [["team", "My Team", Users2]] : []),
    ["unassigned", "Unassigned", UserX],
  ];

  const onDrill = (nav) => {
    const next = new URLSearchParams();
    const t = nav.tab || "all";
    next.set("tab", t);
    ["status", "candidate_type", "recruiter_id", "organization_id", "source", "pipeline_id", "stage_id"].forEach((k) => { if (nav[k]) next.set(k, nav[k]); });
    setParams(next, { replace: true });
    setTab(t);
    setPipeKey((k) => k + 1);
  };

  const selectTab = (k) => { setTab(k); setParams(k === "dashboard" ? {} : { tab: k }, { replace: true }); setPipeKey((x) => x + 1); };

  return (
    <div>
      <PageHeader title="Recruiting" description="Your recruiting command center for candidates, pipeline activity and next actions."
        actions={<Button className="bg-brand hover:bg-brand-strong text-white" onClick={() => setLeadOpen(true)} data-testid="recruiting-add-lead"><UserPlus className="w-4 h-4 mr-1.5" />Add Lead</Button>} />
      <AddLeadDialog open={leadOpen} onClose={() => setLeadOpen(false)} onCreated={() => { setLeadOpen(false); setPipeKey((k) => k + 1); }} />
      <div className="flex flex-wrap items-center gap-x-1 border-b border-slate-200 mb-5" data-testid="recruiting-tabs">
        {TABS.map(([k, l, I]) => (
          <button key={k} data-testid={`rec-tab-${k}`} onClick={() => selectTab(k)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <I className="w-4 h-4" />{l}
          </button>
        ))}
      </div>

      {tab === "dashboard"
        ? <RecruitingDashboard onDrill={onDrill} />
        : tab === "emails" ? <EmailCenter />
        : <Pipeline key={`${tab}-${pipeKey}`} embedded forcedScope={SCOPE_FOR_TAB[tab] || "all"}
            initialFilters={liveFilters} initialPipeline={null} />}
    </div>
  );
}
